import BenchlingConfig from 'BenchlingConfig';
import {BatchSchema} from 'constants/SampleConstants';
import {isUserChangeable} from 'notebook/structured-tables/columns/isReadOnly';
import {
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import getLastSyncedUpstreamValueForColumn from 'notebook/structured-tables/util/getLastSyncedUpstreamValueForColumn';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {decimalRoundingStrategyToDecimalJSRoundingMode} from 'util/Decimals';
import prettyFloat from 'util/prettyFloat';

import isMissingFieldColumn from '../columns/isMissingFieldColumn';
import {isInsertableStructuredTableColumn, isTagValueStructuredTableColumn} from '../columns/TypePredicates';

/**
 * Returns true if the row should be considered as having unsubmitted changes.
 * This could include when the row has not been submitted (i.e. finalized) or if
 * the values contained in the row are different than the
 * `lastSyncedUpstreamValues` which where persisted on the row.
 *
 * If the row has a row delegate column, call getColumnsForRow() to get the delegated columns and pass those
 * in to the columns parameter instead of structuredTable.columns. TODO(BNCH-93275): Remove that caveat by
 * refactoring this function to handle row delegates instead of passing the responsibility to the caller.
 *
 * Agent keep in sync: benchling/notebook/structured_tables/managers/submission_manager.py
 *
 * @param computedRow: the evaluated row to check for unsubmitted changes
 * @param lastSyncedUpstreamValue: the values which were last refreshed or sent to the server
 * @param columns: the columns for the table
 * @param row: the row to check for unsubmitted changes
 * @param schemas: the schemas contained in the table
 */
export default function rowHasUnsubmittedChanges<TRowMetadata>(
  computedRow: Array<EvaluatedCell>,
  row: StructuredTableRow<TRowMetadata>,
  columns: Array<StructuredTableColumn>,
  schemas: Array<Schema | BatchSchema>
): boolean {
  const {isFinalized, lastSyncedUpstreamValues} = row;

  if (!isFinalized) {
    return true;
  }

  if (lastSyncedUpstreamValues === undefined) {
    // Row may or may not have been synced in the past. To avoid showing false indicators, pretend the entire
    // row is up-to-date.
    return false;
  }
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (lastSyncedUpstreamValues === null) {
    // Row has never been synced, so all values are new.
    return true;
  }
  const cellStatuses = getCellHasUnsubmittedChangesForRow(computedRow, row, columns, schemas);
  return cellStatuses.some((hasUnsubmittedChanges) => hasUnsubmittedChanges);
}

/**
 * Returns a boolean array indicating whether each cell in the row should be considered as having unsubmitted changes.
 * This could include when the row has not been submitted (i.e. finalized) or if
 * the values contained in the row are different than the
 * `lastSyncedUpstreamValues` which where persisted on the row.
 *
 * If the row has a row delegate column, call getColumnsForRow() to get the delegated columns and pass those
 * in to the columns parameter instead of structuredTable.columns. TODO(BNCH-93275): Remove that caveat by
 * refactoring this function to handle row delegates instead of passing the responsibility to the caller.
 *
 * Agent keep in sync: benchling/notebook/structured_tables/managers/submission_manager.py
 *
 * @param computedRow: the evaluated row to check for unsubmitted changes
 * @param lastSyncedUpstreamValue: the values which were last refreshed or sent to the server
 * @param columns: the columns for the table
 * @param row: the row to check for unsubmitted changes
 * @param schemas: the schemas contained in the table
 */
export function getCellHasUnsubmittedChangesForRow<TRowMetadata>(
  computedRow: Array<EvaluatedCell>,
  row: StructuredTableRow<TRowMetadata>,
  columns: Array<StructuredTableColumn>,
  schemas: Array<Schema | BatchSchema>
): Array<boolean> {
  const {isFinalized, lastSyncedUpstreamValues} = row;

  if (!isFinalized) {
    return computedRow.map(() => true);
  }

  if (lastSyncedUpstreamValues === undefined) {
    // Row may or may not have been synced in the past. To avoid showing false indicators, pretend the entire
    // row is up-to-date.
    return computedRow.map(() => false);
  }
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (lastSyncedUpstreamValues === null) {
    // Row has never been synced, so all values are new.
    return computedRow.map(() => true);
  }

  // Check each cell individually
  return computedRow.map((cell, i) => {
    const currentColumn = columns[i];
    if (isTagValueStructuredTableColumn(currentColumn) && currentColumn.schemaFieldIsComputed) {
      // Computed fields are never considered unsubmitted
      return false;
    }
    if (!isUserChangeable(currentColumn) || isMissingFieldColumn(currentColumn, schemas)) {
      // Uneditable columns or missing fields shouldn't be considered unsubmitted
      return false;
    }
    return cellHasUnsubmittedChanges(cell, lastSyncedUpstreamValues, columns[i], schemas);
  });
}

// Agent keep in sync: benchling/notebook/structured_tables/managers/submission_manager.py:_cell_has_unsubmitted_changes
export function cellHasUnsubmittedChanges(
  cell: EvaluatedCell,
  lastSyncedUpstreamValues: Array<LastSyncedUpstreamValue>,
  column: StructuredTableColumn,
  schemas: Array<Schema>
): boolean {
  // Computed fields can never carry an unsubmitted user edit: their value is derived server-side, not
  // typed by the user. Skip them here so callers that update cells directly from this result (e.g.
  // pickLiveOrPreservedCells in the keep-formulas resync path) always advance a computed cell to its live
  // value instead of freezing a stale one. Without this, a computed cell whose LSUV is still a COMPUTING
  // placeholder (captured at submit time) is never equivalent to its finished value, so it would be
  // preserved as stale while its LSUV advances — leaving the cell showing the old value with no outdated
  // indicator (CUST-12261). This mirrors the guard in getCellHasUnsubmittedChangesForRow.
  if (isTagValueStructuredTableColumn(column) && column.schemaFieldIsComputed) {
    return false;
  }
  // We never want to consider a computed value as having unsubmitted changes
  if (cell.value.type === BaseTypedValueType.COMPUTING) {
    return false;
  }

  const fieldIntegerId = column.tagSchemaFieldId;
  const schemaField = fieldIntegerId != null ? findSchemaFieldByDBId(schemas, fieldIntegerId) : undefined;
  const lastSyncedUpstreamValue = getLastSyncedUpstreamValueForColumn(lastSyncedUpstreamValues, {
    ...column,
    // override this property in case the column still relies on the old fieldIntegerId instead of the newer schemaFieldApiId
    schemaFieldApiId: schemaField?.apiId ?? null,
  });
  let cellValue = cell.value;
  // Float schema fields can optionally have a decimal precision set on them. If they do, then we want to use that
  // precision to decide if the value is actually different from the last synced value.
  // For example, a field might have a decimal precision of 3, and the LSUV might be 1.234. The user might
  // currently enter the value 1.2340001, which wouldn't actually produce a different value on the Result when
  // submitted. So, we apply the precision to the current value before checking if it would actually be
  // different than the LSUV.
  if (
    BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_SCHEMA_DECIMAL_PRECISION_UNSUBMITTED &&
    schemaField &&
    cellValue.type === FieldType.FLOAT &&
    cellValue.floatValue != null
  ) {
    const schemaDecimalPrecision = schemaField.decimalPrecision;
    if (schemaDecimalPrecision != null) {
      const roundedValue = prettyFloat(
        cellValue.floatValue,
        schemaDecimalPrecision,
        // We must use the rounding strategy configured in BenchlingConfig, as it is the rounding that will
        // be used on the schema field when it is submitted.
        decimalRoundingStrategyToDecimalJSRoundingMode(BenchlingConfig.SCHEMAS_DECIMAL_ROUNDING_STRATEGY)
      );
      cellValue = {...cellValue, value: roundedValue};
    }
  }
  if (lastSyncedUpstreamValue == null) {
    // - In general, if a cell has no lastSyncedUpstreamValue, that indicates a cell that can't be edited
    //   after submission and therefore will never have un-submitted changes.
    // - One exception: A cell in a finalized row will be missing a lastSyncedUpstreamValue if the
    //   column was added to the table after submission, in which case it represents an un-submitted change.
    return isInsertableStructuredTableColumn(column);
  }
  return !areTypedValuesEquivalent(cellValue, lastSyncedUpstreamValue.value);
}

export function findSchemaFieldByDBId(schemas: Array<Schema>, dbId: number): SchemaField | undefined {
  return schemas.flatMap((schema) => schema.allFields).find((field) => field.dbId === dbId);
}
